import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MdArrowBack, MdChevronRight, MdHome } from "react-icons/md";

interface Crumb {
  label: string;
  /** Omitted for the current page — it is not a link. */
  href?: string;
}

const DASHBOARD: Crumb = { label: "Dashboard", href: "/admin" };
const SETTINGS: Crumb = { label: "Settings", href: "/admin/settings" };
const RESTAURANT: Crumb = { label: "Room Restaurant", href: "/admin/restaurant" };

/** Top-level pages, keyed by the first segment after /admin, with the nav
 * group they sit under in the sidebar (if any). Keep in step with Sidebar's
 * nav lists and App.tsx's routes. */
const PAGES: Record<string, { label: string; parent?: Crumb }> = {
  bookings: { label: "Bookings (Rooms)" },
  customers: { label: "Customers" },
  "tax-invoices": { label: "Tax Invoices" },
  events: { label: "Events" },
  quotations: { label: "Quotations" },
  "room-service": { label: "Room Service" },
  housekeeping: { label: "Housekeeping" },
  restaurant: { label: "Room Restaurant" },
  "kitchen-types": { label: "Kitchen Types", parent: RESTAURANT },
  kitchens: { label: "Kitchens", parent: RESTAURANT },
  kot: { label: "KOT", parent: RESTAURANT },
  "restaurant-menu": { label: "Menu", parent: RESTAURANT },
  "kitchen-categories": { label: "Kitchen Categories", parent: RESTAURANT },
  settings: { label: "Settings" },
  halls: { label: "Halls", parent: SETTINGS },
  rooms: { label: "Rooms", parent: SETTINGS },
  packages: { label: "Packages", parent: SETTINGS },
  "event-types": { label: "Event Types", parent: SETTINGS },
  users: { label: "Users", parent: SETTINGS },
  roles: { label: "Roles", parent: SETTINGS },
  branches: { label: "Branches", parent: SETTINGS },
  amenities: { label: "Amenities", parent: SETTINGS },
  buildings: { label: "Buildings", parent: SETTINGS },
  floors: { label: "Floors", parent: SETTINGS },
  facilities: { label: "Facilities", parent: SETTINGS },
  "status-masters": { label: "Status Masters", parent: SETTINGS },
  "food-categories": { label: "Food Categories", parent: SETTINGS },
  "food-menus": { label: "Food Items", parent: SETTINGS },
  "food-packages": { label: "Food Packages", parent: SETTINGS },
  "additional-services": { label: "Additional Services", parent: SETTINGS },
  "guest-service-categories": { label: "Guest Service Categories", parent: SETTINGS },
  "guest-service-items": { label: "Guest Service Items", parent: SETTINGS },
  "rate-plans": { label: "Room Rate Plans", parent: SETTINGS },
  "channel-manager": { label: "OTA Booking", parent: SETTINGS },
  "meal-types": { label: "Room Meal Types", parent: SETTINGS },
  "meal-plans": { label: "Room Meal Plans", parent: SETTINGS },
  "tax-service-settings": { label: "Tax & Service", parent: SETTINGS },
  printers: { label: "Printers", parent: SETTINGS },
};

/** Builds the trail for an /admin path. Detail routes (new / edit / checkout)
 * hang off the list page they belong to, so "back" lands on that list rather
 * than wherever the browser history happens to point. */
const buildTrail = (pathname: string): Crumb[] => {
  const segments = pathname.replace(/^\/admin\/?/, "").split("/").filter(Boolean);
  if (segments.length === 0) return [{ label: "Dashboard" }];

  const [first, second] = segments;
  const bookings: Crumb = { label: "Bookings (Rooms)", href: "/admin/bookings" };
  const events: Crumb = { label: "Events", href: "/admin/events" };
  const quotations: Crumb = { label: "Quotations", href: "/admin/quotations" };

  if (first === "bookings" && second === "new")
    return [DASHBOARD, bookings, { label: "New Booking" }];
  if (first === "bookings" && second === "edit")
    return [DASHBOARD, bookings, { label: "Edit Booking" }];
  if (first === "customers" && second)
    return [DASHBOARD, { label: "Customers", href: "/admin/customers" }, { label: "Customer Details" }];
  if (first === "checkout") return [DASHBOARD, bookings, { label: "Checkout" }];
  if (first === "create-event") return [DASHBOARD, events, { label: "New Event" }];
  if (first === "events" && segments.length > 1)
    return [DASHBOARD, events, { label: "Edit Event" }];
  if (first === "quotations" && second === "new")
    return [DASHBOARD, quotations, { label: "New Quotation" }];
  if (first === "quotations" && segments.length > 1)
    return [DASHBOARD, quotations, { label: "Edit Quotation" }];

  const page = PAGES[first];
  if (!page) return [DASHBOARD];
  return [
    DASHBOARD,
    ...(page.parent ? [page.parent] : []),
    { label: page.label },
  ];
};

const Breadcrumbs: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const trail = buildTrail(pathname);

  // Back goes to the nearest linked ancestor, not history(-1): a page opened
  // from a bookmark or a notification popup has no sensible history entry,
  // and the sidebar can jump anywhere, so the browser's "previous page" is
  // often not the page this one belongs to.
  const parent = [...trail].reverse().find((crumb) => crumb.href);
  const isDashboard = trail.length === 1;

  return (
    <div className="flex items-center gap-3 mb-3 min-w-0">
      {!isDashboard && parent?.href && (
        <button
          type="button"
          onClick={() => navigate(parent.href!)}
          title={`Back to ${parent.label}`}
          className="inline-flex items-center gap-1 flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors"
        >
          <MdArrowBack className="text-sm" />
          Back
        </button>
      )}
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex items-center flex-wrap gap-1 text-xs text-slate-500 dark:text-slate-400">
          {trail.map((crumb, index) => {
            const isLast = index === trail.length - 1;
            return (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1 min-w-0">
                {index > 0 && (
                  <MdChevronRight className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                )}
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                  >
                    {index === 0 && <MdHome className="text-sm" />}
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className="inline-flex items-center gap-1 font-semibold text-slate-800 dark:text-slate-200 truncate"
                  >
                    {index === 0 && <MdHome className="text-sm" />}
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
